// Main SportsPay class
/**
 * @typedef {import('./kreezee_V1.0').Elements} Elements
 */

/**
 * @class
 * @property {string} host - The host URL
 * @property {string} clientSecret - The Client Secret
 * @property {Elements|null} elements - The Elements instance
 */

//card information

class SportsPay {
  constructor() {
    this.elements = null;
  }

  // Create Elements instance
  createElements(options = {}) {
    this.elements = new SportsPayElements(options);
    return this.elements;
  }

  // Confirm setup for payment
  async confirmSetup({ elements, confirmParams }) {
    if (!elements) {
      throw new Error('Elements instance required');
    }

    try {
      const zip = document.getElementById('postalCode')?.value.trim().toUpperCase() ?? '';

      if (zip && !validatePostalCodeFormat(zip)) {
        return {
          error: {
            code: 'payment_failed',
            message: 'Invalid ZIP / postal code',
          },
        };
      }

      const { error, setupIntent } = await elements.submit({ ...confirmParams, zip });

      if (error) {
        return { error };
      }

      // Handle redirect if specified in confirmParams
      if (confirmParams?.return_url) {
        window.location.href = confirmParams.return_url;
      }

      return { setupIntent, zip };
    } catch (err) {
      return {
        error: {
          code: 'processing_error',
          message: err.message,
        },
      };
    }
  }
}

// Elements class to handle payment form elements
class SportsPayElements {
  constructor(options = {}) {
    this.options = options;
    this.paymentElement = this.create('payment', options);
  }

  // Create payment element
  create(type, options = {}) {
    if (type === 'payment') {
      this.paymentElement = PaymentElement(options);
      return this.paymentElement;
    }
    throw new Error(`Unknown element type: ${type}`);
  }

  // Get payment element instance
  getElement(type) {
    if (type === 'payment') {
      return this.paymentElement;
    }
    return null;
  }

  // Submit payment details
  async submit(cardData) {
    if (!this.paymentElement) {
      throw new Error('Payment element not found');
    }

    var sportspay = window.sportspay || undefined;

    if (!sportspay) {
      throw new Error('SportsPay script not loaded');
    }
    var response = await sportspay.sportsPaySubmit(cardData);
    return response;
  }
}

const validatePostalCodeFormat = (value) => {
  if (!value || /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)$/.test(value.trim())) {
    return true;
  } else {
    return false;
  }
};

import React from 'react';
import PropTypes from 'prop-types';
// PaymentElement class for rendering payment form
const PaymentElement = ({ options = {}, organizationName }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [mounted, setMounted] = React.useState(false);
  const [cardNumberError, setCardNumberError] = React.useState('');
  const [expiryDateError, setExpiryDateError] = React.useState('');
  const [cvcError, setCvcError] = React.useState('');
  const [isCardNumberFocused, setIsCardNumberFocused] = React.useState(false);
  const [isExpiryDateFocused, setIsExpiryDateFocused] = React.useState(false);
  const [isCvcFocused, setIsCvcFocused] = React.useState(false);
  const [cardNumberClass, setCardNumberClass] = React.useState('');
  const [expiryDateClass, setExpiryDateClass] = React.useState('');
  const [cvcClass, setCvcClass] = React.useState('');
  const [postalCodeError, setPostalCodeError] = React.useState('');
  const [postalCodeClass, setPostalCodeClass] = React.useState('');

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const validateCardNumber = (value) => {
    var cardName = sportspay.getCreditCardNameByNumber(value);
    if (
      isValid(value) &&
      (cardName === 'Visa' || cardName === 'Mastercard' || cardName === 'Amex' || cardName === 'Discover')
    ) {
      setCardNumberError('');
      setCardNumberClass('is-valid');
    } else {
      setCardNumberError('Invalid card number');
      setCardNumberClass('is-invalid');
    }
  };

  const validateExpiryDate = (value) => {
    const [expiryMonth, expiryYear] = value?.split('/') || [];
    if (sportspay.isExpirationDateValid(expiryMonth, expiryYear)) {
      setExpiryDateError('');
      setExpiryDateClass('is-valid');
    } else {
      setExpiryDateError('Invalid expiry date');
      setExpiryDateClass('is-invalid');
    }
  };

  const validateCvc = (value, cardNumber) => {
    if (sportspay.isSecurityCodeValid(cardNumber, value)) {
      setCvcError('');
      setCvcClass('is-valid');
    } else {
      setCvcError('Invalid CVC');
      setCvcClass('is-invalid');
    }
  };

  const validatePostalCode = (value) => {
    if (validatePostalCodeFormat(value)) {
      setPostalCodeError('');
      setPostalCodeClass(value ? 'is-valid' : '');
      return true;
    } else {
      setPostalCodeError('Invalid ZIP / postal code');
      setPostalCodeClass('is-invalid');
      return false;
    }
  };

  const formatExpiryDate = (value) => {
    const cleanedValue = value.replace(/\D/g, ''); // Remove non-digit characters
    const month = cleanedValue.substring(0, 2);
    const year = cleanedValue.substring(2, 4);
    return month.length > 0 ? `${month}${year.length > 0 ? '/' + year : ''}` : '';
  };

  return (
    <div
      className="sportspay-form needs-validation"
      style={{
        background: 'white',
        borderRadius: '8px',
        ...options.formStyle,
      }}
    >
      <div className="form-group mb-3">
        <label className="input-label form-label">
          <span>Card Number</span>
          <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
        </label>
        <div className="text-input-container">
          <div className="input-group">
            <input
              type="text"
              id="cardNumber"
              required
              pattern="\d{4}\s?\d{4}\s?\d{4}\s?\d{4}"
              placeholder="4242 4242 4242 4242"
              className={`form-control ${cardNumberClass}`}
              onFocus={() => setIsCardNumberFocused(true)}
              onChange={(e) => {
                const value = e.target.value.replace(/\s/g, '');
                validateCardNumber(value);
                e.target.reportValidity();
              }}
              onBlur={(e) => {
                if (isCardNumberFocused) {
                  validateCardNumber(e.target.value.replace(/\s/g, ''));
                  setIsCardNumberFocused(false);
                }
              }}
              style={{
                padding: '8px 14px',
                outline: 'none',
                width: '100%',
                fontSize: '19px',
                paddingRight: '35px', // Add padding for icon
              }}
              maxLength="19"
            />
            <div
              style={{ position: 'absolute', top: '50%', right: '30px', transform: 'translateY(-50%)', zIndex: 999 }}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/5/57/Visa_Inc._logo_%282014%E2%80%932021%29.svg"
                alt="Visa"
                style={{
                  width: '40px',
                  margin: '0 5px',
                }}
              />
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Mastercard_2019_logo.svg"
                alt="MasterCard"
                style={{
                  width: '40px',
                  margin: '0 5px',
                }}
              />
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg"
                alt="American Express"
                style={{
                  width: '30px',
                  margin: '0 5px',
                }}
              />
            </div>
          </div>
        </div>
        {cardNumberError && <div className="feedback-invalid">{cardNumberError}</div>}
      </div>
      <div className="form-row">
        <div className="form-group col-md-6 mb-3">
          <label className="input-label form-label">
            <span>Expiration Date</span>
            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
          </label>
          <div className="text-input-container">
            <div className="input-group">
              <input
                type="text"
                id="expiryDate"
                required
                maxLength={5} // Limit to 5 characters (MM/YY)
                pattern="\d{2}/\d{2}" // Regex pattern for MM/YY
                placeholder="MM/YY"
                className={`form-control ${expiryDateClass}`}
                onFocus={() => setIsExpiryDateFocused(true)}
                onChange={(e) => {
                  const formattedValue = formatExpiryDate(e.target.value);
                  e.target.value = formattedValue; // Update the input value
                  validateExpiryDate(formattedValue);
                  e.target.reportValidity();
                }}
                onBlur={(e) => {
                  if (isExpiryDateFocused) {
                    validateExpiryDate(e.target.value);
                    setIsExpiryDateFocused(false);
                  }
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: '5px',
                  width: '100%',
                  fontSize: '19px',
                  paddingRight: '35px', // Add padding for icon
                }}
              />
            </div>
          </div>
          {expiryDateError && <div className="feedback-invalid">{expiryDateError}</div>}
        </div>
        <div className="form-group col-md-6 mb-3">
          <label className="input-label form-label">
            <span>Security Code</span>
            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
          </label>
          <div className="text-input-container">
            <div className="input-group">
              <input
                type="text"
                id="cvc"
                required
                placeholder="CVC"
                className={`form-control ${cvcClass}`}
                maxLength={4} // Limit to 4 digits
                pattern="\d{3,4}" // Regex pattern for 3 or 4 digits
                onFocus={() => setIsCvcFocused(true)}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Allow only digits
                  e.target.value = value; // Update the input value
                  const cardNumberInput = document.getElementById('cardNumber');
                  const cardNumber = cardNumberInput ? cardNumberInput.value : '';
                  validateCvc(value, cardNumber);
                  e.target.reportValidity();
                }}
                onBlur={(e) => {
                  if (isCvcFocused) {
                    const cardNumberInput = document.getElementById('cardNumber');
                    const cardNumber = cardNumberInput ? cardNumberInput.value : '';
                    validateCvc(e.target.value, cardNumber);
                    setIsCvcFocused(false);
                  }
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: '5px',
                  width: '100%',
                  fontSize: '19px',
                  paddingRight: '35px', // Add padding for icon
                }}
              />
            </div>
          </div>
          {cvcError && <div className="feedback-invalid">{cvcError}</div>}
        </div>
      </div>
      <div className="form-group mb-3">
        <label className="input-label form-label">
          <span>ZIP / Postal Code</span>
        </label>
        <div className="text-input-container">
          <div className="input-group">
            <input
              type="text"
              id="postalCode"
              autoComplete="postal-code"
              maxLength={10}
              pattern="\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z][ \-]?\d[A-Za-z]\d"
              placeholder="12345 / A1A 1A1"
              className={`form-control ${postalCodeClass}`}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
                validatePostalCode(e.target.value);
              }}
              onBlur={(e) => {
                e.target.value = e.target.value.toUpperCase();
                validatePostalCode(e.target.value);
              }}
              style={{
                padding: '8px 14px',
                borderRadius: '5px',
                width: '100%',
                fontSize: '19px',
                textTransform: 'uppercase',
              }}
            />
          </div>
        </div>
        {postalCodeError && <div className="feedback-invalid">{postalCodeError}</div>}
      </div>
      <span className="text-muted">
        By providing your card information, you allow {organizationName} to charge your card for future payments in
        accordance with their terms.
      </span>
    </div>
  );
};

PaymentElement.propTypes = {
  options: PropTypes.shape({
    formStyle: PropTypes.object,
  }),
  organizationName: PropTypes.string,
};

PaymentElement.defaultProps = {
  options: {},
  organizationName: '',
};

// React hook for SportsPay
function useSportspay() {
  return new SportsPay();
}

// React hook for Elements
function useElements() {
  const sportspay = useSportspay();
  return sportspay.createElements();
}

async function loadSportspayScript(clientSecret) {
  const existingScript = document.querySelector(`script[src*="${window.configs.sp_Script_File_name}"]`);

  if (existingScript) {
    existingScript.parentNode?.removeChild(existingScript);
  }

  const script = document.createElement('script');
  script.src = `${clientSecret.replace(/\/$/, '')}/${window.configs.sp_Script_File_name}`; // Adjust the source as needed
  script.async = true;
  document.head.appendChild(script);

  return script;
}

export { SportsPay, SportsPayElements, PaymentElement, useSportspay, useElements, loadSportspayScript };
